import { graphql } from '@api/generated';
import { RecordingAction } from '@api/generated/graphql';
import { useMutation } from '@apollo/client';
import ToastModule from '@modules/ToastModule';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { RecordingStatus } from '@modules/SoftPhoneModule/lib/getInitialRecordingState';

/** Tooltip messages for recording status */
export enum RecordingTooltip {
  STARTED = 'Stop recording',
  SUPPORTED_BUT_NOT_RECORDING = 'Your local laws may require you to notify your call recipients that calls are recorded. Please consult with independent legal counsel on the compliance requirements in your jurisdiction.',
  NOT_SUPPORTED = 'Recording for this call type is not yet supported.',
}

/** Returns the tooltip for a given recording status. */
export const getRecordingTooltip = (status: RecordingStatus): string => {
  switch (status) {
    case RecordingStatus.NOT_SUPPORTED:
      return RecordingTooltip.NOT_SUPPORTED;
    case RecordingStatus.STARTED:
      return RecordingTooltip.STARTED;
    default:
      return RecordingTooltip.SUPPORTED_BUT_NOT_RECORDING;
  }
};

export const PERFORM_RECORDING_ACTION_MUTATION = graphql(`
  mutation performRecordingAction(
    $kazooCallId: String!
    $action: RecordingAction!
  ) {
    performRecordingAction(kazooCallId: $kazooCallId, action: $action)
  }
`);

const showRecordingError = () => {
  ToastModule.showToast({
    status: 'critical',
    title: 'Failed to perform recording action',
    toastBody: 'Please try again',
  });
};

const useRecording = (callId: string) => {
  const { callList } = usePhoneState();
  const recordingState = callList[callId]
    ? callList[callId].recordingState
    : { status: RecordingStatus.NOT_STARTED, disabled: false };
  const [mutate, { loading }] = useMutation(PERFORM_RECORDING_ACTION_MUTATION);

  const toggleRecording = async (kazooCallId: string) => {
    let action: RecordingAction;
    let newStatus: RecordingStatus;

    switch (recordingState.status) {
      case RecordingStatus.PAUSED:
        action = RecordingAction.Resume;
        newStatus = RecordingStatus.STARTED;
        break;
      case RecordingStatus.STARTED:
        action = RecordingAction.Pause;
        newStatus = RecordingStatus.PAUSED;
        break;
      default: // covers RecordingStatus.NOT_STARTED
        action = RecordingAction.Start;
        newStatus = RecordingStatus.STARTED;
    }

    try {
      const result = await mutate({
        variables: {
          kazooCallId,
          action,
        },
      });

      if (result.data?.performRecordingAction) {
        SoftPhoneModule.setRecordingStatus(callId, newStatus);
      } else {
        showRecordingError();
      }
    } catch (error) {
      showRecordingError();
    }
  };

  return {
    toggleRecording,
    isRecording: recordingState.status === RecordingStatus.STARTED,
    isRecordingLoading: loading,
    isRecordingDisabled: recordingState.disabled,
    recordingTooltip: getRecordingTooltip(recordingState.status),
  };
};

export default useRecording;
